/**
 * SearchableSelect - A dropdown with multi-word fuzzy search
 * Each word in the query must appear somewhere in the option label (any order).
 */
import { useState, useRef, useEffect } from "react";
import { Search, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import { fuzzyMatch } from "../../lib/searchUtils";

const SearchableSelect = ({
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  options = [],       // [{ value, label, group?, subtitle? }]
  className = "",
  triggerClassName = "",
  "data-testid": testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [open]);

  // Multi-word search: every word must appear somewhere in the label
  const filtered = search.trim()
    ? options.filter((opt) => {
        const combined = (opt.label || "") + " " + (opt.subtitle || "");
        return fuzzyMatch(search, combined);
      })
    : options;

  // Group items if groups exist
  const hasGroups = options.some((o) => o.group);
  const groups = hasGroups
    ? [...new Set(filtered.map((o) => o.group).filter(Boolean))]
    : [];

  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${!value ? "text-muted-foreground" : ""} ${triggerClassName}`}
          data-testid={testId}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`p-0 ${className}`} align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        </div>
        <div className="max-h-[40vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No results found</p>
          ) : hasGroups ? (
            groups.map((group) => (
              <div key={group}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {group}
                </div>
                {filtered
                  .filter((o) => o.group === group)
                  .map((opt) => (
                    <button
                      key={opt.value}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left"
                      onClick={() => { onValueChange(opt.value); setOpen(false); }}
                      data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                    >
                      <Check className={`w-4 h-4 shrink-0 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                      <div className="min-w-0">
                        <p className="truncate">{opt.label}</p>
                        {opt.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{opt.subtitle}</p>
                        )}
                      </div>
                    </button>
                  ))}
              </div>
            ))
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent cursor-pointer text-left"
                onClick={() => { onValueChange(opt.value); setOpen(false); }}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <Check className={`w-4 h-4 shrink-0 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                <div className="min-w-0">
                  <p className="truncate">{opt.label}</p>
                  {opt.subtitle && (
                    <p className="text-xs text-muted-foreground truncate">{opt.subtitle}</p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { SearchableSelect };
